import React, { useCallback, useState, useEffect, useRef } from "react"

import { Post } from "@fider/models"
import { Avatar, Button, Form } from "@fider/components"
import { SignInModal } from "@fider/components"

import { cache, actions, Failure, Fider } from "@fider/services"
import { HStack } from "@fider/components/layout"
import { i18n } from "@lingui/core"
import { Trans } from "@lingui/react/macro"

import { useFider } from "@fider/hooks"
import { useAttachments } from "@fider/hooks/useAttachments"
import CommentEditor from "@fider/components/common/form/CommentEditor"
import { AiSuggestion } from "@fider/components/common/form/AiSuggestion"

interface CommentInputProps {
  post: Post
}

const CACHE_TITLE_KEY = "CommentInput-Comment-Title-"
const TYPING_PING_MS = 2500
const CACHE_ATTACHMENTS_KEY = "CommentInput-Comment-Attachments-"

export const CommentInput = (props: CommentInputProps) => {
  const getCacheKey = (cachePrefix: string) => `${cachePrefix}${props.post.id}`

  const getContentFromCache = () => {
    return cache.session.get(getCacheKey(CACHE_TITLE_KEY))
  }

  const COMMENT_MAX_LENGTH = 4000

  const fider = useFider()
  const [isSignInModalOpen, setIsSignInModalOpen] = useState(false)
  const [error, setError] = useState<Failure | undefined>(undefined)
  const [isClient, setIsClient] = useState(false)
  const [contentLength, setContentLength] = useState((cache.session.get(`${CACHE_TITLE_KEY}${props.post.id}`) ?? "").length)
  const [content, setContent] = useState<string>(cache.session.get(`${CACHE_TITLE_KEY}${props.post.id}`) ?? "")
  const [editorValue, setEditorValue] = useState({ value: "", version: 0 })

  // Use the attachments hook
  const { attachments, handleImageUploaded, getImageSrc, clearAttachments } = useAttachments({
    cacheKey: getCacheKey(CACHE_ATTACHMENTS_KEY),
    maxAttachments: 2,
  })

  // Check if we're running on the client after component mounts
  useEffect(() => {
    setIsClient(true)
  }, [])

  const hideModal = () => setIsSignInModalOpen(false)
  const clearError = () => setError(undefined)

  const [isChecking, setIsChecking] = useState(false)

  const submit = async () => {
    clearError()

    const content = getContentFromCache()

    // Tùy biến KHCN: kiểm tra nội dung trước, không đạt thì báo ngay tại ô nhập
    setIsChecking(true)
    const check = await actions.checkContent("comment", undefined, content || "", { postNumber: props.post.number })
    setIsChecking(false)
    if (check.ok && check.data.verdict !== "ok") {
      setError({
        errors: [
          {
            field: "content",
            message: i18n._({
              id: "khcn.comment.check.blocked",
              message: "Not ready to publish: {reason} Edit your comment and try again.",
              values: { reason: check.data.reason },
            }),
          },
        ],
      })
      return
    }

    const result = await actions.createComment(props.post.number, content || "", attachments)
    if (result.ok) {
      clearAttachments()
      cache.session.remove(getCacheKey(CACHE_TITLE_KEY))
      if (!result.data.isApproved) {
        cache.session.set("COMMENT_CREATED_MODERATION", "true")
        if (result.data.moderationReason) {
          cache.session.set("COMMENT_MODERATION_REASON", result.data.moderationReason)
        }
      }
      location.reload()
    } else {
      setError(result.error)
    }
  }

  const editorFocused = () => {
    if (!fider.session.isAuthenticated) {
      setIsSignInModalOpen(true)
    }
  }

  const hasContent = true

  // Báo "đang nhập" cho người khác, thưa để không dội API
  const lastTypingPing = useRef(0)
  const pingTyping = () => {
    const now = Date.now()
    if (!Fider.session.isAuthenticated || now - lastTypingPing.current < TYPING_PING_MS) {
      return
    }
    lastTypingPing.current = now
    void actions.sendTyping(props.post.number)
  }

  const commentChanged = useCallback((value: string): void => {
    cache.session.set(getCacheKey(CACHE_TITLE_KEY), value)
    setContentLength(value.length)
    setContent(value)
    if (value.trim() !== "") {
      pingTyping()
    }
  }, [])

  // Áp dụng gợi ý AI (hoặc hoàn tác / làm lại) vào ô bình luận
  const applySuggestion = (_title: string | undefined, text: string) => {
    commentChanged(text)
    setEditorValue((current) => ({ value: text, version: current.version + 1 }))
  }

  const isOverLimit = contentLength > COMMENT_MAX_LENGTH

  return (
    <>
      <SignInModal isOpen={isSignInModalOpen} onClose={hideModal} />
      <HStack spacing={4} className="c-comment-input" align="start">
        {Fider.session.isAuthenticated && <Avatar user={Fider.session.user} size="large" />}
        <div className="c-comment-input-card">
          <Form error={error}>
            {!fider.session.isAuthenticated ? (
              // Khách chưa đăng nhập chỉ xem: không hiện ô soạn bình luận
              <button type="button" className="c-comment-input__signin" onClick={() => setIsSignInModalOpen(true)}>
                <Trans id="khcn.comment.signin">Sign in to join the discussion</Trans>
              </button>
            ) : isClient ? (
              <>
                <CommentEditor
                  field="content"
                  disabled={false}
                  onChange={commentChanged}
                  onFocus={editorFocused}
                  initialValue={getContentFromCache()}
                  placeholder={i18n._({ id: "showpost.commentinput.placeholder", message: "Leave a comment" })}
                  maxAttachments={2}
                  maxImageSizeKB={5 * 1024}
                  maxLength={COMMENT_MAX_LENGTH}
                  onGetImageSrc={getImageSrc}
                  onImageUploaded={handleImageUploaded}
                  externalValue={editorValue}
                  mentionPostNumber={props.post.number}
                />
                <AiSuggestion kind="comment" text={content} onApply={applySuggestion} />

                {hasContent && (
                  <>
                    <Button disabled={isOverLimit || isChecking} variant="primary" onClick={submit} className="mt-4">
                      {isChecking ? <Trans id="khcn.comment.checking">Checking...</Trans> : <Trans id="action.postcomment">Post</Trans>}
                    </Button>
                  </>
                )}
              </>
            ) : (
              <div className="comment-input-placeholder p-2">{i18n._({ id: "showpost.commentinput.placeholder", message: "Leave a comment" })}</div>
            )}
          </Form>
        </div>
      </HStack>
    </>
  )
}
